import { Avatar, Badge, Tooltip, Popover } from "antd";
import classNames from "classnames";
import { useEffect, useState, useMemo, useCallback, memo } from "react";
import type { Conversation } from "@interfaces/chat.interface";
import { formatUnreadCount } from "@utils/formatUnreadCount";
import { highlightText } from "@utils/textHighlight";
import { useChatStore } from "@stores/chatStore";
import { useAppSelector } from "@stores/redux/store";
import { getTagColor, CHANNELS } from "@common";
import type { Agent } from "@interfaces/api.interface";
import styles from "./styles.module.less";
import FacebookIcon from "@assets/facebook-icon.svg";
import ZaloIcon from "@assets/zalo-icon.svg";
import { unassignTagsFromConversation } from "@api/services/facebook";
import ShopeeIcon from "@assets/shopee-icon.svg";
import {
  PushpinFilled,
  CheckCircleFilled,
  CloseCircleFilled,
  OpenAIOutlined,
  TagOutlined,
  TagsTwoTone,
  TagTwoTone,
} from "@ant-design/icons";
import { ConversationItemMenu } from "./ConversationItemMenu";
import { getAvatarGradient, getAvatarLetter } from "@utils/avatarUtils";
import { parseZaloEmoji } from "@utils/zaloEmojiParser";

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onSelect: () => void;
  onPin: () => void;
  onMute: () => void;
  onDelete?: () => void;
  onOpenFanpageMenu?: () => void;
  tags?: Array<{ description: string; id: number; tagName: string }>;
  agent?: Agent;
  searchValue?: string;
  folderStatus?: string;
  movedToTab?: string;
}

const formatTimeAgo = (date: Date | string): string => {
  const now = new Date();
  const messageDate = new Date(date);
  const diffInSeconds = Math.floor(
    (now.getTime() - messageDate.getTime()) / 1000
  );

  if (diffInSeconds < 60) {
    return "vừa xong";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} phút trước`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} giờ trước`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ngày trước`;
  } else if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} tuần trước`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} tháng trước`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} năm trước`;
  }
};

const getPlatformIcon = (channel: Conversation["channel"]) => {
  switch (channel) {
    case CHANNELS.FACEBOOK:
      return FacebookIcon;
    case CHANNELS.ZALO:
      return ZaloIcon;
    case CHANNELS.SHOPEE:
      return ShopeeIcon;
    default:
      return null;
  }
};

const ConversationTag = ({
  tags,
  onRemove,
}: {
  tags: Array<{ description: string; id: number; tagName: string }>;
  onRemove?: (tagId: number) => void;
}) => {
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {tags.map((tag) => {
        const color = getTagColor(tag.id);
        return (
          <div
            key={tag.id}
            className={styles.tag + " " + styles["tag-" + color]}
          >
            <TagOutlined /> {tag.tagName}
            {onRemove && (
              <CloseCircleFilled
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(tag.id);
                }}
                style={{ marginLeft: 4, cursor: "pointer", opacity: 0.7 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ConversationItem = memo(
  ({
    conversation,
    isActive,
    onSelect,
    onPin,
    onDelete,
    onOpenFanpageMenu,
    tags: tagsFromProps = [],
    agent,
    searchValue = "",
    folderStatus,
  }: ConversationItemProps) => {
    
    const lastMessage = conversation.lastMessage;
    const isOwnMessage =
      lastMessage?.sender?.name === "Bạn" ||
      lastMessage?.sender?.name === "You";
    const platformIcon = getPlatformIcon(conversation.channel);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const previewContent = useMemo(() => {
      if (!lastMessage) return "";
      const messageType = lastMessage.type;
      if (messageType === "audio") {
        return "[Âm thanh]";
      }
      if (messageType === "image") {
        return "[Hình ảnh]";
      }
      if (messageType === "video") {
        return "[Video]";
      }
      if (messageType === "file") {
        return "[Tệp đính kèm]";
      }
      if (messageType === "link") {
        return "Liên kết";
      }
      if (
        lastMessage.meta?.provider === "zalo" &&
        lastMessage.meta?.originalMessageType === "CALL_REQUEST"
      ) {
        return "[Cuộc gọi]";
      }
      const body = typeof lastMessage.body === "string" ? lastMessage.body : "";
      if (messageType === "text" && lastMessage.meta?.provider === "zalo") {
        return parseZaloEmoji(body);
      }
      return body;
    }, [lastMessage]);

    const selectedFanpageName = useChatStore(
      (state) => state.selectedFanpageName
    );
    const facebookPages = useAppSelector((state) => state.fanpage.pages);
    const [tags, setTags] =
      useState<Array<{ description: string; id: number; tagName: string }>>(
        tagsFromProps
      );
    const updateConversationTagsMap = useChatStore(
      (state) => state.updateConversationTagsMap
    );
    const [currentAgent, setCurrentAgent] = useState<Agent | undefined>(agent);

    useEffect(() => {
      setTags(tagsFromProps);
    }, [tagsFromProps]);

    const selectedPageId = useMemo(() => {
      if (selectedFanpageName && facebookPages.length > 0) {
        const selectedPage = facebookPages.find(
          (page) => page.name === selectedFanpageName
        );
        return selectedPage?.id;
      }
      return null;
    }, [selectedFanpageName, facebookPages]);

    const handlePlatformIconClick = useCallback(
      (e: React.MouseEvent<HTMLImageElement>) => {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        onOpenFanpageMenu?.();
      },
      [onOpenFanpageMenu]
    );

    useEffect(() => {
      setTags(tagsFromProps);
    }, [tagsFromProps]);

    useEffect(() => {
      setCurrentAgent(agent);
    }, [agent]);

    const handleContextMenu = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDropdownOpen(true);
      },
      []
    );

    const handleTagsChange = useCallback(
      (
        newTags: Array<{ description: string; id: number; tagName: string }>
      ) => {
        setTags(newTags);
      },
      []
    );

    const handleAgentChange = useCallback(
      (newAgent: Agent | null | undefined) => {
        setCurrentAgent(newAgent || undefined);
      },
      []
    );

    const handleRemoveTag = useCallback(
      async (tagId: number) => {
        try {
          const platform =
            conversation.channel === CHANNELS.ZALO
              ? "Zalo"
              : conversation.channel === CHANNELS.SHOPEE
              ? "Shopee"
              : "Facebook";

          await unassignTagsFromConversation({
            externalId: conversation.id,
            platform,
            tagIds: [tagId],
            pageId: selectedPageId || undefined,
          });

          const newTags = tags.filter((t) => t.id !== tagId);
          setTags(newTags);
          updateConversationTagsMap({
            [conversation.id]: newTags,
          });
        } catch (error) {
          console.error("Failed to remove tag:", error);
        }
      },
      [
        conversation.id,
        conversation.channel,
        selectedPageId,
        tags,
        updateConversationTagsMap,
      ]
    );

    const agentPopoverContent = useMemo(
      () =>
        currentAgent ? (
          <div className={styles.agentPopover}>
            <Avatar
              src={currentAgent.avatarUrl}
              size={40}
              className={styles.popoverAvatar}
            >
              {currentAgent.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <div className={styles.popoverInfo}>
              <span className={styles.popoverName}>{currentAgent.name}</span>
              <span className={styles.popoverRole}>
                <CheckCircleFilled
                  style={{ color: "#52c41a", fontSize: "10px" }}
                />
                Nhân viên hỗ trợ
              </span>
            </div>
          </div>
        ) : null,
      [currentAgent]
    );

    const tagsPopoverContent = useMemo(
      () =>
        tags.length > 0 ? (
          <div className={styles.tagsPopover}>
            <div
              style={{
                fontWeight: "bold",
                borderBottom: "1px solid #e5e5e5",
                marginBottom: 10,
              }}
            >
              {tags.length > 1 ? (
                <div>
                  {" "}
                  <TagsTwoTone style={{ transform: "rotate(-180deg)" }} /> Danh
                  sách nhãn được gắn
                </div>
              ) : (
                <div>
                  {" "}
                  <TagTwoTone style={{ transform: "rotate(-180deg)" }} /> Nhãn
                  được gắn
                </div>
              )}
            </div>
            <ConversationTag tags={tags} onRemove={handleRemoveTag} />
          </div>
        ) : (
          <div className={styles.paramPopoverEmpty}>Chưa có nhãn nào</div>
        ),
      [tags, handleRemoveTag]
    );

    return (
      <div className={styles.itemWrapper}>
        <div
          className={classNames(styles.conversationItem, {
            [styles.active]: isActive,
          })}
          onClick={onSelect}
          onContextMenu={handleContextMenu}
          role="button"
        >
        <div className={styles.avatarWrapper}>
          {conversation.avatar !== "" ? (
            <Avatar src={conversation.avatar} size={40} />
          ) : (
            <Avatar
              style={{
                background: getAvatarGradient(
                  conversation.name?.charAt(0) || "A"
                ),
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "16px",
              }}
            >
              {getAvatarLetter(conversation.name)}
            </Avatar>
          )}
          {platformIcon && (
            <img
              src={platformIcon}
              alt={conversation.channel}
              className={styles.platformIcon}
              onClick={handlePlatformIconClick}
              role="button"
            />
          )}
        </div>

        <div className={styles.body}>
          <div className={styles.title}>
            <div className={styles.titleLeft}>
              <span className={styles.name}>
                {searchValue
                  ? highlightText(conversation.name, searchValue)
                  : conversation.name}
                {currentAgent && (
                  <Popover content={agentPopoverContent}>
                    <OpenAIOutlined
                      style={{ marginLeft: 6, color: "#1890ff" }}
                    />
                  </Popover>
                )}
                {tags.length > 0 && (
                  <Popover content={tagsPopoverContent}>
                    {tags.length > 1 ? (
                      <TagsTwoTone
                        style={{
                          marginLeft: currentAgent ? 3 : 6,
                          transform: "rotate(-180deg)",
                          color: "#fa2549",
                        }}
                      />
                    ) : (
                      <TagTwoTone
                        style={{
                          marginLeft: currentAgent ? 3 : 6,
                          transform: "rotate(-180deg)",
                          color: "#f20a0a",
                        }}
                      />
                    )}
                  </Popover>
                )}
              </span>
            </div>

            <span className={styles.time}>
              <span
                className={
                  conversation.channel !== CHANNELS.ZALO
                    ? styles.timeText
                    : styles.zaloTimeText
                }
              >
                {lastMessage?.createdAt
                  ? formatTimeAgo(lastMessage.createdAt)
                  : ""}
              </span>
              {conversation.channel !== CHANNELS.ZALO && (
                <ConversationItemMenu
                  conversation={{
                    isPinned: conversation.isPinned,
                    lastMessage: conversation.lastMessage,
                  }}
                  tags={tags}
                  agent={currentAgent}
                  selectedPageId={selectedPageId || null}
                  folderStatus={folderStatus}
                  onPin={onPin}
                  onDelete={onDelete}
                  onTagsChange={handleTagsChange}
                  onAgentChange={handleAgentChange}
                  open={dropdownOpen}
                  onOpenChange={setDropdownOpen}
                />
              )}
            </span>
          </div>
          <div className={styles.preview}>
            {isOwnMessage && <span className={styles.sender}>[Bạn]: </span>}
            <span className={styles.text}>{previewContent}</span>
          </div>
        </div>

        {conversation.isPinned && <PushpinFilled className={styles.pinIcon} />}

        {folderStatus === "DONE" ? (
          conversation.unreadCount > 0 ? (
            <Badge
              count={formatUnreadCount(conversation.unreadCount)}
              className={styles.badge}
              overflowCount={99}
            >
              <Tooltip title="Đánh dấu xong">
                <CheckCircleFilled className={styles.doneBadge} />
              </Tooltip>
            </Badge>
          ) : (
            <div className={styles.doneBadgeWrapper}>
              <Tooltip title="Đánh dấu xong">
                <CheckCircleFilled className={styles.doneBadge} />
              </Tooltip>
            </div>
          )
        ) : conversation.unreadCount > 0 ? (
          <Badge
            count={formatUnreadCount(conversation.unreadCount)}
            className={styles.badge}
          />
        ) : null}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    const prevTags = prevProps.tags || [];
    const nextTags = nextProps.tags || [];
    const tagsEqual =
      prevTags.length === nextTags.length &&
      prevTags.every((tag, index) => tag.id === nextTags[index]?.id) &&
      nextTags.every((tag, index) => tag.id === prevTags[index]?.id);

    return (
      prevProps.conversation.id === nextProps.conversation.id &&
      prevProps.conversation.lastMessage?.id ===
        nextProps.conversation.lastMessage?.id &&
      prevProps.conversation.unreadCount ===
        nextProps.conversation.unreadCount &&
      prevProps.conversation.isPinned === nextProps.conversation.isPinned &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.conversation.name === nextProps.conversation.name &&
      prevProps.conversation.avatar === nextProps.conversation.avatar &&
      tagsEqual &&
      prevProps.agent?.id === nextProps.agent?.id &&
      prevProps.searchValue === nextProps.searchValue &&
      prevProps.folderStatus === nextProps.folderStatus
    );
  }
);
